import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/library/card";
import {
  ExternalLink,
  FileText,
  Loader2,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { useCourseForm } from "@/features/universityAndCourse/contexts/FormCourseContext";
import { useRef, useState } from "react";
import { toast } from "sonner";

const MAX_BROCHURE_SIZE_MB = 10;

const formatBytes = (bytes: number) => {
  if (!bytes) return "";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(2)} MB`;
};

export default function CourseBrochure() {
  const {
    pendingBrochure,
    setPendingBrochure,
    existingBrochure,
    isBrochureUploading,
    uploadBrochureNow,
    deleteBrochureNow,
    isBrochureDeleting,
    courseId,
  } = useCourseForm();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleFileClick = () => {
    if (isBrochureUploading) return;
    fileInputRef.current?.click();
  };

  const acceptFile = async (file: File) => {
    if (file.type !== "application/pdf") {
      toast.error("Brochure must be a PDF file");
      return;
    }
    if (file.size > MAX_BROCHURE_SIZE_MB * 1024 * 1024) {
      toast.error(`Brochure must be smaller than ${MAX_BROCHURE_SIZE_MB} MB`);
      return;
    }

    if (courseId) {
      // Edit mode → upload immediately (matches the pattern of ChildDocUpload)
      try {
        await uploadBrochureNow(file);
      } catch {
        // toast already shown by upload mutation
      }
    } else {
      // Create mode → queue, will upload after course is created on save
      setPendingBrochure(file);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) acceptFile(file);
    event.target.value = "";
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) acceptFile(file);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleRemovePending = (event: React.MouseEvent) => {
    event.stopPropagation();
    setPendingBrochure(null);
  };

  const handleDeleteExisting = async () => {
    try {
      await deleteBrochureNow();
    } finally {
      setConfirmDelete(false);
    }
  };

  // What to render:
  // 1. Uploading right now → show in-flight card (with spinner)
  // 2. Pending file (create mode) → show queued card
  // 3. Existing uploaded brochure → show with View / Replace
  // 4. Otherwise → empty dropzone
  const showUploading = isBrochureUploading;
  const showDeleting = !showUploading && isBrochureDeleting;
  const showPending =
    !showUploading && !showDeleting && pendingBrochure !== null;
  const showExisting =
    !showUploading &&
    !showDeleting &&
    !showPending &&
    existingBrochure !== null;

  return (
    <Card className="bg-white border-[#e6e8e7] w-full gap-4 p-4">
      <CardHeader className="px-0 py-0 gap-0">
        <CardTitle className="font-medium text-base text-black tracking-[-0.32px] leading-normal">
          Course Brochure{" "}
          <span className="font-normal text-sm text-[#697571] tracking-[-0.56px] leading-[1.35]">
            (optional)
          </span>
        </CardTitle>
      </CardHeader>

      <CardContent className="px-0 py-0">
        <div className="flex flex-col gap-3 w-full">
          {showUploading && (
            <div className="bg-[#fcfcfc] border-[#6fb99c] border-[1.2px] rounded-xl p-3 flex items-center gap-3">
              <div className="bg-[#eaf5f1] rounded-lg p-2 size-10 flex items-center justify-center shrink-0">
                <Loader2 className="size-5 text-[#218760] animate-spin" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#051912] tracking-[-0.56px] leading-[1.35] truncate">
                  Uploading brochure…
                </p>
                <p className="text-xs text-[#697571] tracking-[-0.24px]">
                  Please wait
                </p>
              </div>
            </div>
          )}

          {showDeleting && (
            <div className="bg-[#fcfcfc] border-[#cdd1d0] border-[1.2px] rounded-xl p-3 flex items-center gap-3">
              <div className="bg-[#fdecef] rounded-lg p-2 size-10 flex items-center justify-center shrink-0">
                <Loader2 className="size-5 text-[#da2a46] animate-spin" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#051912] tracking-[-0.56px] leading-[1.35] truncate">
                  Deleting brochure…
                </p>
                <p className="text-xs text-[#697571] tracking-[-0.24px]">
                  Please wait
                </p>
              </div>
            </div>
          )}

          {showPending && (
            <div className="bg-[#fcfcfc] border-[#6fb99c] border-[1.2px] rounded-xl p-3 flex items-center gap-3">
              <div className="bg-[#eaf5f1] rounded-lg p-2 size-10 flex items-center justify-center shrink-0">
                <FileText
                  className="size-5 text-[#218760]"
                  strokeWidth={1.5}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#051912] tracking-[-0.56px] leading-[1.35] truncate">
                  {pendingBrochure!.name}
                </p>
                <p className="text-xs text-[#697571] tracking-[-0.24px]">
                  {formatBytes(pendingBrochure!.size)} · Will upload on save
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={handleFileClick}
                  className="text-xs text-[#218760] hover:underline tracking-[-0.24px]"
                >
                  Replace
                </button>
                <button
                  type="button"
                  aria-label="Remove file"
                  onClick={handleRemovePending}
                  className="size-7 rounded-full hover:bg-[#f1f4f3] flex items-center justify-center text-[#697571]"
                >
                  <X className="size-4" />
                </button>
              </div>
            </div>
          )}

          {showExisting && (
            <div className="bg-[#fcfcfc] border-[#cdd1d0] border-[1px] rounded-xl p-3 flex items-center gap-3">
              <div className="bg-[#eaf5f1] rounded-lg p-2 size-10 flex items-center justify-center shrink-0">
                <FileText
                  className="size-5 text-[#218760]"
                  strokeWidth={1.5}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#051912] tracking-[-0.56px] leading-[1.35] truncate">
                  {existingBrochure!.name || existingBrochure!.fileName}
                </p>
                <p className="text-xs text-[#697571] tracking-[-0.24px]">
                  {formatBytes(existingBrochure!.size)} · Uploaded
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {confirmDelete ? (
                  <>
                    <span className="text-xs text-[#697571] tracking-[-0.24px]">
                      Delete?
                    </span>
                    <button
                      type="button"
                      onClick={handleDeleteExisting}
                      className="text-xs text-[#da2a46] hover:underline tracking-[-0.24px]"
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(false)}
                      className="text-xs text-[#697571] hover:underline tracking-[-0.24px]"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <a
                      href={existingBrochure!.fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-[#218760] hover:underline tracking-[-0.24px] flex items-center gap-1"
                    >
                      <ExternalLink className="size-3.5" />
                      View
                    </a>
                    <button
                      type="button"
                      onClick={handleFileClick}
                      className="text-xs text-[#218760] hover:underline tracking-[-0.24px]"
                    >
                      Replace
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(true)}
                      className="text-xs text-[#da2a46] hover:underline tracking-[-0.24px] flex items-center gap-1"
                    >
                      <Trash2 className="size-3.5" />
                      Delete
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {!showUploading && !showPending && !showExisting && (
            <div
              onClick={handleFileClick}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`bg-[#fcfcfc] border-[1.2px] border-dashed rounded-xl p-4 flex flex-col gap-2 items-center justify-center cursor-pointer transition-colors ${
                isDragging
                  ? "border-[#218760] bg-[#eaf5f1]"
                  : "border-[#6fb99c] hover:bg-[#fafafa]"
              }`}
            >
              <div className="bg-[#eaf5f1] rounded-lg p-2 size-10 flex items-center justify-center">
                <Upload className="size-5 text-[#218760]" strokeWidth={1.5} />
              </div>
              <p className="text-sm text-[#374741] tracking-[-0.56px] leading-[1.35] text-center">
                Upload File (PDF)
              </p>
              <p className="text-xs text-[#697571] tracking-[-0.24px] text-center">
                {courseId
                  ? "Click or drop a PDF to upload immediately"
                  : "Drag & drop or click — uploads after you save the course"}
                {" · "}max {MAX_BROCHURE_SIZE_MB} MB
              </p>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>
      </CardContent>
    </Card>
  );
}
